import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface AddYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddYearDialog({ open, onOpenChange }: AddYearDialogProps) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState("");
  const [description, setDescription] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get existing years to exclude from dropdown
  const { data: existingYears = [] } = useQuery({
    queryKey: ['/api/years'],
    enabled: open,
  });

  const existingYearNumbers = (existingYears as any[]).map((y: any) => parseInt(y.fullYear));

  // Generate year options (current year - 5 to current year + 5)
  const yearOptions = [];
  for (let year = currentYear - 5; year <= currentYear + 5; year++) {
    if (!existingYearNumbers.includes(year)) {
      yearOptions.push(year);
    }
  }

  const createYearMutation = useMutation({
    mutationFn: async (yearData: {
      id: string;
      fullYear: number;
      description?: string;
    }) => {
      const response = await apiRequest('POST', '/api/years', yearData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      toast({
        title: "Year created",
        description: `Year ${selectedYear} has been added successfully.`,
      });
      handleClose();
    },
    onError: (error: any) => {
      console.error("Year creation error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create year. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedYear) {
      toast({
        title: "Year required",
        description: "Please select a year to add.",
        variant: "destructive",
      });
      return;
    }

    const yearNumber = parseInt(selectedYear);
    const yearId = yearNumber.toString().slice(-2); // Get last 2 digits

    createYearMutation.mutate({
      id: yearId,
      fullYear: yearNumber,
      description: description.trim() || undefined,
    });
  };

  const handleClose = () => {
    setSelectedYear("");
    setDescription("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md navy-card border-gray-600">
        <DialogHeader>
          <DialogTitle className="text-xl text-gray-100">Add New Year</DialogTitle>
          <DialogDescription className="text-gray-400">
            Create a new year to organize your projects.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="year" className="text-gray-300">
              Year <span className="text-red-400">*</span>
            </Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-100">
                <SelectValue placeholder="Select a year" />
              </SelectTrigger>
              <SelectContent className="navy-card border-gray-600">
                {yearOptions.length > 0 ? (
                  yearOptions.map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-white">
                      {year}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-gray-400">
                    All available years have been added
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-gray-300">
              Description (Optional)
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for this year..."
              className="bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 min-h-[80px]"
            />
          </div>

          <DialogFooter className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createYearMutation.isPending || yearOptions.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createYearMutation.isPending ? "Creating..." : "Create Year"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}